// useCursorEditorCellPxResize — shared state + resize-grip plumbing for the
// cursor editor's variable cell size (作者 staging フィードバック 2026-09-19
// follow-up: 「パネルをリサイズして大きくしたい」).
//
// 両方のフローティング chrome (Classic `CursorEditorPopup` / Modern
// `ModernCursorEditorPanel`) がこの 1 本の hook を使うことで:
//   - 初期値を `cursorEditorCellPxPreference.ts` (localStorage) から読む.
//   - `cellPx` state と、`CursorEditorPanel` の「小/中/大」プリセットボタンから
//     呼べる `setCellPx` を提供する (viewport clamp + 即時永続化).
//   - パネル右下の角ドラッグ用の `resizeGrip` ハンドラを提供する。ドラッグの
//     pointer-capture 配管自体は既存の `usePanelDragGrip` (= フロートパネルの
//     移動ドラッグと同じ hook) をそのまま再利用する — 「掴んで動かす」その場の
//     移動先だけを resize 用に差し替える (grabOffset を pointerdown 時の
//     `{ clientX, clientY }` = リサイズ用の `pointerStart` として流用する)。
//
// 2 系統 (プリセットボタン / 角ドラッグ) が同じ state を更新するので、
// どちらで変えても他方の表示 (ボタンの aria-pressed / グリッドの実サイズ) に
// 自動的に反映される。

import { useCallback, useEffect, useRef, useState } from 'react';

import { createCursorEditorCellPxPreferenceStore } from '../../../infrastructure/cursorEditorCellPxPreference.js';
import {
  type CursorEditorResizeAnchor,
  clampCursorEditorCellPxToViewport,
  nextCursorEditorCellPxFromResize,
} from './cursorEditorCellPx.js';
import { type PanelDragGripHandlers, usePanelDragGrip } from './usePanelDragGrip.js';

export interface UseCursorEditorCellPxResizeResult {
  readonly cellPx: number;
  /** プリセットボタン (小/中/大) から呼ぶ。viewport clamp + 即時永続化まで行う. */
  readonly setCellPx: (next: number) => void;
  /** パネル右下の角グリップに spread する pointer ハンドラ一式. */
  readonly resizeGrip: PanelDragGripHandlers;
}

const readViewport = (): { width: number; height: number } =>
  typeof window === 'undefined'
    ? { width: Number.POSITIVE_INFINITY, height: Number.POSITIVE_INFINITY }
    : { width: window.innerWidth, height: window.innerHeight };

export const useCursorEditorCellPxResize = (): UseCursorEditorCellPxResizeResult => {
  const storeRef = useRef(createCursorEditorCellPxPreferenceStore());
  const [cellPx, setCellPxState] = useState<number>(() => storeRef.current.get());
  const cellPxRef = useRef(cellPx);
  cellPxRef.current = cellPx;

  // 初回 mount: 保存値が今の viewport に収まらなければ丸める (前回より小さい
  // 画面で開いたケースの救済). 永続値そのものは書き換えない (= 大画面に戻れば
  // 元の希望サイズが復活する、previewSizePreference の流儀).
  useEffect(() => {
    const { width, height } = readViewport();
    const fit = clampCursorEditorCellPxToViewport(cellPxRef.current, width, height);
    if (fit !== cellPxRef.current) setCellPxState(fit);
    // mount 時 1 回のみ評価すればよい (viewport 変化への追従は resize イベント
    // までは不要 — パネルは開き直しが軽い一時窓のため).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCellPx = useCallback((next: number) => {
    const { width, height } = readViewport();
    const clamped = clampCursorEditorCellPxToViewport(next, width, height);
    setCellPxState(clamped);
    storeRef.current.set(clamped);
  }, []);

  const anchorRef = useRef<CursorEditorResizeAnchor | null>(null);
  const resizeGrip = usePanelDragGrip({
    // resize には「掴んだ点 − 要素左上」の意味は要らないので、pointerdown 時点の
    // client 座標をそのまま `pointerStart` として流用する (`onDragStart` へ渡る).
    measureGrabOffset: (e) => ({ x: e.clientX, y: e.clientY }),
    onDragStart: (pointerStart) => {
      anchorRef.current = { originCellPx: cellPxRef.current, pointerStart };
    },
    onDragMove: (clientX, clientY) => {
      const anchor = anchorRef.current;
      if (anchor === null) return;
      const next = nextCursorEditorCellPxFromResize(anchor, { x: clientX, y: clientY });
      const { width, height } = readViewport();
      setCellPxState(clampCursorEditorCellPxToViewport(next, width, height));
    },
    onDragEnd: (committed) => {
      anchorRef.current = null;
      if (committed) storeRef.current.set(cellPxRef.current);
    },
  });

  return { cellPx, setCellPx, resizeGrip };
};
